import { useState, useEffect, useCallback } from 'react';
import { externalCostsService, profitabilityService } from '../../services/externalCostsService';
import toast from 'react-hot-toast';
import api from '../../services/api';

export default function useAccountingData({ locationId, activeTab }) {
  const [loading, setLoading] = useState(true);

  // Dane
  const [invoices, setInvoices] = useState([]);
  const [allInvoices, setAllInvoices] = useState([]);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [kasaOperations, setKasaOperations] = useState([]);
  const [kasaSaldo, setKasaSaldo] = useState(null);
  const [locations, setLocations] = useState([]);
  const [selectedFilterLocation, setSelectedFilterLocation] = useState(locationId || '');

  // Synchronizuj filtr lokalizacji z globalna lokalizacja (przelacznik). Bez tego
  // selectedFilterLocation zostaje z wartoscia poczatkowa i widok kasa-bank/ksiegowosc
  // pokazuje stara lokalizacje po przelaczeniu (fetch-e zaleza od selectedFilterLocation).
  useEffect(() => {
    setSelectedFilterLocation(locationId || '');
  }, [locationId]);

  // Filtry faktur
  const [invoiceFilters, setInvoiceFilters] = useState({
    search: '',
    status: '',
    dateFrom: '',
    dateTo: '',
    paymentStatus: '',
    page: 1,
    perPage: 20
  });

  // Filtry kasa
  const [kasaFilters, setKasaFilters] = useState({
    dateFrom: '',
    dateTo: '',
    typPlatnosci: ''
  });

  const [pagination, setPagination] = useState({ total: 0, pages: 1 });
  const [markingPaid, setMarkingPaid] = useState(null);

  // State dla kosztow zewnetrznych
  const [costs, setCosts] = useState([]);
  const [costCategories, setCostCategories] = useState([]);
  const [costsLoading, setCostsLoading] = useState(false);
  const [showCostModal, setShowCostModal] = useState(false);
  const [editingCost, setEditingCost] = useState(null);
  const [costForm, setCostForm] = useState({
    location_id: '', category_id: '', nazwa: '', kwota_netto: '', stawka_vat: 23,
    data_kosztu: new Date().toISOString().split('T')[0], kontrahent: '', cykliczny: false, cykl_typ: ''
  });
  const [costsFilters, setCostsFilters] = useState({
    category_id: '', status: '', date_from: '', date_to: ''
  });

  // State dla rentownosci
  const [profitabilityData, setProfitabilityData] = useState(null);
  const [profitabilityLoading, setProfitabilityLoading] = useState(false);
  const today = new Date();
  const firstDayOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
  const [profDateFrom, setProfDateFrom] = useState(firstDayOfMonth.toISOString().split('T')[0]);
  const [profDateTo, setProfDateTo] = useState(today.toISOString().split('T')[0]);

  // Pobierz lokalizacje
  useEffect(() => {
    api.get('/locations/')
      .then(response => {
        const data = response.data;
        if (data.success || Array.isArray(data.locations)) {
          setLocations(data.locations || data.data || []);
        }
      })
      .catch(err => console.error('Error fetching locations:', err));
  }, []);

  // Pobierz faktury
  const fetchInvoices = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('page', invoiceFilters.page);
      params.append('per_page', invoiceFilters.perPage);

      if (invoiceFilters.search) params.append('search', invoiceFilters.search);
      if (invoiceFilters.status) params.append('status', invoiceFilters.status);
      if (invoiceFilters.dateFrom) params.append('date_from', invoiceFilters.dateFrom);
      if (invoiceFilters.dateTo) params.append('date_to', invoiceFilters.dateTo);
      if (selectedFilterLocation) params.append('location_id', selectedFilterLocation);

      const response = await api.get(`/purchase-invoices?${params}`);
      const data = response.data;

      if (data.success) {
        let filteredInvoices = data.data?.invoices || data.invoices || data.data || [];

        if (invoiceFilters.paymentStatus) {
          const now = new Date();
          filteredInvoices = filteredInvoices.filter(inv => {
            const dueDate = inv.data_platnosci ? new Date(inv.data_platnosci) : null;
            const isPaid = inv.status === 'zatwierdzona';

            switch (invoiceFilters.paymentStatus) {
              case 'paid': return isPaid;
              case 'unpaid': return !isPaid;
              case 'overdue': return !isPaid && dueDate && dueDate < now;
              default: return true;
            }
          });
        }

        setInvoices(filteredInvoices);
        const paginationData = data.data?.pagination || data.pagination || { total: filteredInvoices.length, pages: 1 };
        setPagination(paginationData);
      }
    } catch (error) {
      console.error('Error fetching invoices:', error);
    }
  }, [invoiceFilters, selectedFilterLocation]);

  // Pobierz wszystkie faktury (bez filtrow) do obliczen statystyk
  const fetchAllInvoices = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('per_page', '1000');
      if (selectedFilterLocation) params.append('location_id', selectedFilterLocation);

      const response = await api.get(`/purchase-invoices?${params}`);
      const data = response.data;

      if (data.success) {
        const allInv = data.data?.invoices || data.invoices || data.data || [];
        setAllInvoices(allInv);
      }
    } catch (error) {
      console.error('Error fetching all invoices:', error);
    }
  }, [selectedFilterLocation]);

  // Pobierz statystyki faktur
  const fetchInvoiceStats = useCallback(async () => {
    try {
      const params = selectedFilterLocation ? `?location_id=${selectedFilterLocation}` : '';
      const response = await api.get(`/purchase-invoices/menu${params}`);
      const data = response.data;

      if (data.success) {
        setInvoiceStats(data.data);
      }
    } catch (error) {
      console.error('Error fetching invoice stats:', error);
    }
  }, [selectedFilterLocation]);

  // Pobierz operacje kasa/bank
  const fetchKasaOperations = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append('limit', '100');

      if (kasaFilters.dateFrom) params.append('date_from', kasaFilters.dateFrom);
      if (kasaFilters.dateTo) params.append('date_to', kasaFilters.dateTo);
      if (kasaFilters.typPlatnosci) params.append('typ_platnosci', kasaFilters.typPlatnosci);
      if (selectedFilterLocation) params.append('location_id', selectedFilterLocation);

      const response = await api.get(`/kasa-bank/operacje?${params}`);
      const data = response.data;

      if (data.success) {
        const operations = data.data?.operacje ||
                          (Array.isArray(data.data) ? data.data : []) ||
                          (Array.isArray(data.operacje) ? data.operacje : []);
        setKasaOperations(operations);
      } else {
        setKasaOperations([]);
      }
    } catch (error) {
      console.error('Error fetching kasa operations:', error);
      setKasaOperations([]);
    }
  }, [kasaFilters, selectedFilterLocation]);

  // Pobierz salda kasa/bank
  const fetchKasaSaldo = useCallback(async () => {
    try {
      const params = selectedFilterLocation ? `?location_id=${selectedFilterLocation}` : '';
      const response = await api.get(`/kasa-bank/saldo${params}`);
      const data = response.data;

      if (data.success) {
        setKasaSaldo(data.data);
      }
    } catch (error) {
      console.error('Error fetching kasa saldo:', error);
    }
  }, [selectedFilterLocation]);

  // Pobierz koszty zewnetrzne
  const fetchCosts = useCallback(async () => {
    setCostsLoading(true);
    try {
      const filters = {
        per_page: 100,
        ...costsFilters
      };
      if (selectedFilterLocation) filters.location_id = selectedFilterLocation;

      const [costsRes, catsRes] = await Promise.all([
        externalCostsService.getCosts(filters),
        externalCostsService.getCategories()
      ]);

      if (costsRes.success) {
        const items = costsRes.data?.items || [];
        setCosts(items);
      }
      if (catsRes.success) setCostCategories(catsRes.data || []);
    } catch (error) {
      console.error('Error fetching costs:', error);
    }
    setCostsLoading(false);
  }, [costsFilters, selectedFilterLocation]);

  // Pobierz dane rentownosci
  const fetchProfitability = useCallback(async () => {
    setProfitabilityLoading(true);
    try {
      const res = await profitabilityService.getSummary(profDateFrom, profDateTo);
      if (res.success) setProfitabilityData(res.data);
    } catch (error) {
      console.error('Error fetching profitability:', error);
    }
    setProfitabilityLoading(false);
  }, [profDateFrom, profDateTo]);

  // Oznacz fakture jako zaplacona
  const markAsPaid = async (invoice, e) => {
    e.stopPropagation();

    if (!window.confirm(`Czy na pewno chcesz oznaczyć fakturę ${invoice.numer_faktury} jako zapłaconą?`)) {
      return;
    }

    setMarkingPaid(invoice.id);

    try {
      const response = await api.put(`/purchase-invoices/${invoice.id}`, {
        status: 'zatwierdzona'
      });

      const data = response.data;
      if (data.success) {
        await new Promise(resolve => setTimeout(resolve, 300));
        await Promise.all([
          fetchInvoices(),
          fetchAllInvoices(),
          fetchInvoiceStats()
        ]);
      } else {
        toast.error('Błąd: ' + (data.error || 'Nie udało się oznaczyć faktury jako zapłaconej'));
      }
    } catch (error) {
      console.error('Error marking invoice as paid:', error);
      toast.error('Błąd podczas oznaczania faktury jako zapłaconej');
    } finally {
      setMarkingPaid(null);
    }
  };

  // Handlers kosztow
  const handleSaveCost = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...costForm,
        location_id: costForm.location_id || null,
        category_id: parseInt(costForm.category_id),
        kwota_netto: parseFloat(costForm.kwota_netto)
      };

      let result;
      if (editingCost) {
        result = await externalCostsService.updateCost(editingCost.id, payload);
      } else {
        result = await externalCostsService.createCost(payload);
      }

      if (result.success) {
        setShowCostModal(false);
        setEditingCost(null);
        setCostForm({
          location_id: '', category_id: '', nazwa: '', kwota_netto: '', stawka_vat: 23,
          data_kosztu: new Date().toISOString().split('T')[0], kontrahent: '', cykliczny: false, cykl_typ: ''
        });
        fetchCosts();
      } else {
        toast.error('Błąd zapisu: ' + (result.message || 'Nieznany błąd'));
      }
    } catch (err) {
      console.error('handleSaveCost - error:', err);
      toast.error('Błąd: ' + err.message);
    }
  };

  const handlePayCost = async (cost) => {
    if (!window.confirm(`Oznaczyć "${cost.nazwa}" jako zapłacony?`)) return;
    try {
      await externalCostsService.payCost(cost.id, { sposob_platnosci: 'przelew' });
      fetchCosts();
    } catch (err) {
      toast.error('Błąd: ' + err.message);
    }
  };

  const handleDeleteCost = async (cost) => {
    if (!window.confirm(`Czy na pewno usunąć "${cost.nazwa}"?`)) return;
    try {
      await externalCostsService.deleteCost(cost.id);
      fetchCosts();
    } catch (err) {
      toast.error('Błąd: ' + err.message);
    }
  };

  const openEditCost = (cost) => {
    setEditingCost(cost);
    setCostForm({
      location_id: cost.location_id || '',
      category_id: cost.category_id || '',
      nazwa: cost.nazwa || '',
      kwota_netto: cost.kwota_netto || '',
      stawka_vat: cost.stawka_vat || 23,
      data_kosztu: cost.data_kosztu || new Date().toISOString().split('T')[0],
      kontrahent: cost.kontrahent || '',
      cykliczny: cost.cykliczny || false,
      cykl_typ: cost.cykl_typ || ''
    });
    setShowCostModal(true);
  };

  const openNewCost = () => {
    setEditingCost(null);
    setCostForm({
      location_id: '', category_id: '', nazwa: '', kwota_netto: '', stawka_vat: 23,
      data_kosztu: new Date().toISOString().split('T')[0], kontrahent: '', cykliczny: false, cykl_typ: ''
    });
    setShowCostModal(true);
  };

  const closeCostModal = () => {
    setShowCostModal(false);
    setEditingCost(null);
  };

  // Laduj dane kosztow gdy zakladka aktywna
  useEffect(() => {
    if (activeTab === 'costs') {
      fetchCosts();
    }
  }, [activeTab, fetchCosts]);

  // Laduj dane rentownosci gdy zakladka aktywna
  useEffect(() => {
    if (activeTab === 'profitability') {
      fetchProfitability();
    }
  }, [activeTab, fetchProfitability]);

  // Laduj dane glowne
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchInvoices(),
        fetchAllInvoices(),
        fetchInvoiceStats(),
        fetchKasaOperations(),
        fetchKasaSaldo()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchInvoices, fetchAllInvoices, fetchInvoiceStats, fetchKasaOperations, fetchKasaSaldo]);

  // Obliczenia dla dashboardu
  const calculateOverviewStats = () => {
    const now = new Date();
    const thisMonth = now.getMonth();
    const thisYear = now.getFullYear();

    const safeInvoices = Array.isArray(allInvoices) ? allInvoices : [];
    const safeKasaOperations = Array.isArray(kasaOperations) ? kasaOperations : [];

    const overdueInvoices = safeInvoices.filter(inv => {
      if (inv.status === 'zatwierdzona' || inv.status === 'odrzucona') return false;
      const dueDate = inv.data_platnosci ? new Date(inv.data_platnosci) : null;
      return dueDate && dueDate < now;
    });

    const weekFromNow = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const dueSoonInvoices = safeInvoices.filter(inv => {
      if (inv.status === 'zatwierdzona' || inv.status === 'odrzucona') return false;
      const dueDate = inv.data_platnosci ? new Date(inv.data_platnosci) : null;
      return dueDate && dueDate >= now && dueDate <= weekFromNow;
    });

    const totalToPay = safeInvoices
      .filter(inv => inv.status !== 'zatwierdzona' && inv.status !== 'odrzucona')
      .reduce((sum, inv) => sum + (parseFloat(inv.suma_brutto) || 0), 0);

    const monthlyOperations = safeKasaOperations.filter(op => {
      const opDate = new Date(op.data_operacji);
      return opDate.getMonth() === thisMonth && opDate.getFullYear() === thisYear;
    });

    const monthlyIncome = monthlyOperations
      .filter(op => op.typ_operacji === 'KP')
      .reduce((sum, op) => sum + (parseFloat(op.kwota) || 0), 0);

    const monthlyExpense = monthlyOperations
      .filter(op => op.typ_operacji === 'KW')
      .reduce((sum, op) => sum + (parseFloat(op.kwota) || 0), 0);

    return {
      overdueCount: overdueInvoices.length,
      overdueValue: overdueInvoices.reduce((sum, inv) => sum + (parseFloat(inv.suma_brutto) || 0), 0),
      dueSoonCount: dueSoonInvoices.length,
      dueSoonValue: dueSoonInvoices.reduce((sum, inv) => sum + (parseFloat(inv.suma_brutto) || 0), 0),
      totalToPay,
      monthlyIncome,
      monthlyExpense,
      monthlyBalance: monthlyIncome - monthlyExpense
    };
  };

  const refreshAll = () => {
    fetchInvoices();
    fetchAllInvoices();
    fetchInvoiceStats();
    fetchKasaOperations();
    fetchKasaSaldo();
  };

  return {
    loading,
    // Invoices
    invoices, allInvoices, invoiceStats, invoiceFilters, setInvoiceFilters,
    pagination, markingPaid, markAsPaid,
    // Locations
    locations, selectedFilterLocation, setSelectedFilterLocation,
    // Kasa
    kasaOperations, kasaSaldo, kasaFilters, setKasaFilters,
    // Costs
    costs, costCategories, costsLoading, showCostModal, editingCost,
    costForm, setCostForm, costsFilters, setCostsFilters,
    handleSaveCost, handlePayCost, handleDeleteCost, openEditCost, openNewCost, closeCostModal,
    fetchCosts,
    // Profitability
    profitabilityData, profitabilityLoading,
    profDateFrom, setProfDateFrom, profDateTo, setProfDateTo, fetchProfitability,
    // General
    calculateOverviewStats, refreshAll
  };
}
